import {
  Box, Grid, Stack, useColorModeValue,
} from '@chakra-ui/react';
import React, { ReactNode } from 'react';
import { ITerm } from '../../../lib/graphql';
import CourseList from './CourseList';
import TermHeader from '../../../components/TermHeader';

const Term = ({ term }: { term: ITerm }): ReactNode => {
  const containerBg = useColorModeValue('white', 'gray.800');
  const patternOpacity = useColorModeValue(0.03, 0.05);

  if (!term) { return null; }

  return (
    <Box
      width="100%"
      py={[12, 16, 20]}
      px={0}
      position="relative"
      overflow="hidden"
    >
      {/* Decorative Background Pattern */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        opacity={patternOpacity}
        backgroundImage="radial-gradient(circle at 20% 50%, currentColor 2px, transparent 2px), radial-gradient(circle at 80% 50%, currentColor 2px, transparent 2px)"
        backgroundSize="60px 60px"
        backgroundPosition="0 0, 30px 30px"
      />

      {/* Floating Geometric Shapes */}
      <Box
        position="absolute"
        top="10%"
        left="5%"
        width="100px"
        height="100px"
        borderRadius="full"
        bg="blue.100"
        opacity={0.1}
        filter="blur(2px)"
      />
      <Box
        position="absolute"
        top="60%"
        right="8%"
        width="80px"
        height="80px"
        borderRadius="lg"
        bg="purple.100"
        opacity={0.15}
        transform="rotate(45deg)"
        filter="blur(1px)"
      />
      <Box
        position="absolute"
        bottom="20%"
        left="15%"
        width="60px"
        height="60px"
        borderRadius="md"
        bg="indigo.100"
        opacity={0.1}
        transform="rotate(30deg)"
        filter="blur(1px)"
      />

      <Box position="relative" zIndex={1} width="100%" px={4}>
        <Stack spacing={[8, 12, 16]} width="100%" maxW="1200px" mx="auto">
          {/* Term Header with enhanced background */}
          <Box
            textAlign="center"
            position="relative"
            py={[4, 6, 8]}
            px={[4, 6]}
            borderRadius="2xl"
            bg="whiteAlpha.50"
            backdropFilter="blur(10px)"
            border="1px solid"
            borderColor="whiteAlpha.200"
            boxShadow="0 8px 32px rgba(0,0,0,0.1)"
          >
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              borderRadius="2xl"
              background="linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)"
            />
            <Box position="relative" zIndex={1}>
              <TermHeader term={term} />
            </Box>
          </Box>

          {/* Course Grid with enhanced container */}
          {term?.courseGroups && (
            <Box
              bg={containerBg}
              borderRadius={['0', '3xl']}
              p={[3, 6, 10]}
              shadow="2xl"
              border="1px solid"
              borderColor={useColorModeValue('gray.200', 'gray.600')}
              position="relative"
              overflow="hidden"
              width="100%"
            >
              {/* Subtle gradient overlay */}
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                height="200px"
                background="linear-gradient(180deg, rgba(99, 102, 241, 0.02) 0%, transparent 100%)"
                borderTopRadius="3xl"
              />

              {/* Grid background pattern */}
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                opacity={0.01}
                backgroundImage="linear-gradient(rgba(99, 102, 241, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(99, 102, 241, 0.1) 1px, transparent 1px)"
                backgroundSize="40px 40px"
              />

              <Box position="relative" zIndex={1} p={[1, 3, 4]}>
                <Grid
                  gridTemplateColumns={[
                    '1fr',
                    'repeat(2, 1fr)',
                    'repeat(3, 1fr)',
                    'repeat(4, 1fr)',
                  ]}
                  gap={[3, 6, 8]}
                  justifyContent="center"
                  alignItems="stretch"
                  px={[2, 0]}
                >
                  <CourseList courses={term.courseGroups} />
                </Grid>
              </Box>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default Term;
